import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sidebar } from '../../components/Sidebar';
import { ActivityFeed } from '../../components/ActivityFeed';
import { Sparkles, Briefcase, IndianRupee, ArrowRight, Inbox, CheckCircle2, Clock } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const HERO_BG = 'https://static.prod-images.emergentagent.com/jobs/9de54d98-d977-4d52-98c6-87def6f7dbb6/images/270719a556826637cdb21fff7e6a54d54775e71246a523c63143781c80b4f5f3.png';

const statusMeta = {
  submitted: { label: 'Under Review', color: '#FFB800', icon: Clock },
  under_review: { label: 'Under Review', color: '#FFB800', icon: Clock },
  assigned: { label: 'Assigned', color: '#0055FF', icon: CheckCircle2 },
  in_progress: { label: 'In Progress', color: '#0055FF', icon: Briefcase },
  completed: { label: 'Completed', color: '#00C972', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', color: '#FF3B30', icon: Inbox },
};

export const BrandDashboard = () => {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/brand/campaigns`, { withCredentials: true });
        setCampaigns(data || []);
      } catch (e) { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  const total = campaigns.length;
  const active = campaigns.filter((c) => ['assigned', 'in_progress'].includes(c.status)).length;
  const pending = campaigns.filter((c) => ['submitted', 'under_review'].includes(c.status)).length;
  const totalSpend = campaigns.reduce((sum, c) => sum + (c.budget || 0), 0);

  const stats = [
    { label: 'Campaigns', value: total, accent: '#FFFFFF' },
    { label: 'Under Review', value: pending, accent: '#FFB800' },
    { label: 'Active', value: active, accent: '#00C972' },
    { label: 'Budget Allocated', value: `₹${totalSpend.toLocaleString()}`, accent: '#FF451A' },
  ];

  return (
    <div className="flex min-h-screen bg-white dark:bg-[#0A0B0D]">
      <Sidebar />
      <div className="flex-1 relative overflow-x-hidden" data-testid="brand-dashboard-page">
        {/* Hero with cinematic bg */}
        <div className="relative">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${HERO_BG})` }}
          ></div>
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/40 via-[#050505]/70 to-[#050505]"></div>

          <div className="relative z-10 px-10 pt-12 pb-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF451A] mb-4">
                Welcome to your control room
              </div>
              <h1 className="font-display text-5xl md:text-6xl font-medium text-[#0F0F0F] dark:text-white leading-[1.05] tracking-tight">
                Good morning, <span className="italic text-[#FF451A]">{user?.name?.split(' ')[0] || 'Brand'}</span>.
              </h1>
              <p className="font-body text-[#6B6B6B] dark:text-zinc-400 mt-3 max-w-xl text-lg">
                {user?.company_name || 'Your brand'} — orchestrating elite creator partnerships across the Vyvre network.
              </p>
              <div className="mt-7 flex gap-3 flex-wrap">
                <Link to="/brand/discover" className="btn-tactile btn-tactile-primary px-7 py-3.5 rounded-md flex items-center gap-2" data-testid="start-discovery-button">
                  <Sparkles size={16} /> Launch Discovery
                </Link>
                <Link to="/brand/campaigns" className="btn-tactile btn-tactile-secondary px-7 py-3.5 rounded-md flex items-center gap-2">
                  <Briefcase size={16} /> My Campaigns
                </Link>
              </div>
            </motion.div>

            {/* Glass stats */}
            <motion.div
              className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10"
              initial="hidden" animate="visible"
              variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
            >
              {stats.map((s) => (
                <motion.div
                  key={s.label}
                  variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                  className="glass rounded-2xl p-6 group"
                  data-testid={`brand-stat-${s.label.toLowerCase().replace(/ /g, '-')}`}
                >
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#6B6B6B] dark:text-zinc-500 mb-2">{s.label}</div>
                  <div className="font-mono text-3xl font-light text-[#0F0F0F] dark:text-white group-hover:text-[#FF451A] transition-colors">
                    {s.value}
                  </div>
                  <div className="mt-3 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Body */}
        <div className="px-10 pb-12 space-y-8">
          {/* AI Discovery feature card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="card-luxe rounded-3xl p-10 relative overflow-hidden"
          >
            <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-[#FF451A]/15 blur-[100px]"></div>
            <div className="relative z-10 grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF451A] mb-3 flex items-center gap-2">
                  <Sparkles size={11} /> AI Matching Engine
                </div>
                <h2 className="font-display text-4xl text-[#0F0F0F] dark:text-white tracking-tight leading-tight mb-4">
                  The future of creator discovery, <span className="italic text-[#FF451A]">curated for you.</span>
                </h2>
                <p className="text-[#6B6B6B] dark:text-zinc-400 mb-6">
                  Our proprietary matching algorithm scans 33+ vetted creators across 18 categories — surfacing the perfect fit by audience overlap, content tone, and historical ROI.
                </p>
                <Link to="/brand/discover" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#FF451A] hover:text-white transition-colors group">
                  Discover creators
                  <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
              <div className="hidden md:flex justify-center">
                <div className="w-64 h-64 rounded-full bg-gradient-to-br from-[#FF451A]/20 via-[#FF451A]/5 to-transparent flex items-center justify-center glow-pulse">
                  <Sparkles size={80} className="text-[#FF451A]" />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Recent campaigns */}
          <div className="card-luxe rounded-2xl p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#6B6B6B] dark:text-zinc-500 mb-1">Pipeline</div>
                <h3 className="font-display text-2xl text-[#0F0F0F] dark:text-white">Recent campaigns</h3>
              </div>
              <Link to="/brand/campaigns" className="font-mono text-xs uppercase tracking-[0.2em] text-[#FF451A] hover:text-white transition-colors">
                View all →
              </Link>
            </div>

            {loading ? (
              <div className="py-10 text-center text-[#6B6B6B] dark:text-zinc-500">Loading…</div>
            ) : campaigns.length === 0 ? (
              <div className="py-12 text-center">
                <Inbox className="mx-auto text-zinc-700 mb-3" size={40} />
                <h4 className="font-display text-xl text-[#0F0F0F] dark:text-white mb-1">No campaigns yet</h4>
                <p className="text-sm text-[#6B6B6B] dark:text-zinc-500 mb-5">Discover creators and send your first brief.</p>
                <Link to="/brand/discover" className="btn-tactile btn-tactile-primary px-6 py-2.5 rounded-md inline-flex items-center gap-2">
                  <Sparkles size={14} /> Start Discovery
                </Link>
              </div>
            ) : (
              <motion.div
                className="space-y-2"
                initial="hidden" animate="visible"
                variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
              >
                {campaigns.slice(0, 5).map((c) => {
                  const meta = statusMeta[c.status] || statusMeta.submitted;
                  const Icon = meta.icon;
                  return (
                    <motion.div
                      key={c.id}
                      variants={{ hidden: { opacity: 0, x: -10 }, visible: { opacity: 1, x: 0 } }}
                      className="flex items-center gap-4 p-4 rounded-xl bg-white/[0.02] border border-black/[0.08] dark:border-white/5 hover:border-[#FF451A]/30 hover:bg-white/[0.04] transition-all group"
                      data-testid="brand-campaign-row"
                    >
                      <div className="w-10 h-10 rounded-lg bg-black/40 border border-black/[0.08] dark:border-white/5 flex items-center justify-center" style={{ color: meta.color }}>
                        <Icon size={16} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-[#0F0F0F] dark:text-white truncate">{c.campaign_name}</div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#6B6B6B] dark:text-zinc-500 mt-0.5">
                          {c.creator_count} creators · ₹{(c.budget || 0).toLocaleString()} · {(c.platforms || []).join(', ')}
                        </div>
                      </div>
                      <span
                        className="font-mono px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.15em] font-semibold"
                        style={{ backgroundColor: `${meta.color}15`, color: meta.color, border: `1px solid ${meta.color}30` }}
                      >
                        {meta.label}
                      </span>
                    </motion.div>
                  );
                })}
              </motion.div>
            )}
          </div>

          {/* Activity */}
          <BrandActivityCard />
        </div>
      </div>
    </div>
  );
};

const BrandActivityCard = () => (
  <div className="card-luxe rounded-2xl overflow-hidden">
    <div className="p-6 border-b border-black/[0.08] dark:border-white/5">
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#6B6B6B] dark:text-zinc-500 mb-1">Live</div>
      <h3 className="font-display text-2xl text-[#0F0F0F] dark:text-white">Activity feed</h3>
    </div>
    <div className="bg-transparent">
      <ActivityFeed limit={15} title="" emptyText="No platform activity yet." />
    </div>
  </div>
);
